import React, { useEffect, useState, useCallback, Suspense, lazy } from "react";
import { Helmet } from "react-helmet";
import Window from "../../../../elements/window";
import { formatDate } from "../../../../../formatDate";
import axios from "axios";
import ExpirinOutletsModal from "./expiringOutletsModal/";
import "../../style.scss";
import { useAppContext } from "../../../../../../AppContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { height } from "@fortawesome/free-brands-svg-icons/fa42Group";

// Lazy load all slide components for better performance
const Slide1 = lazy(() => import("./views/slide1"));
const Slide2 = lazy(() => import("./views/slide2"));
const Slide3 = lazy(() => import("./views/slide3"));
const Slide4 = lazy(() => import("./views/slide4"));
const Slide5 = lazy(() => import("./views/slide5"));
const Slide7 = lazy(() => import("./views/slide7"));
const Slide8 = lazy(() => import("./views/slide8"));
const Slide9 = lazy(() => import("./views/slide9"));
const Slide10 = lazy(() => import("./views/slide10"));
const Slide11 = lazy(() => import("./views/slide11"));
const Slide12 = lazy(() => import("./views/slide12"));
const Slide13 = lazy(() => import("./views/slide13"));
const Slide14 = lazy(() => import("./views/slide14"));
const Slide15 = lazy(() => import("./views/slide15"));
const Slide16 = lazy(() => import("./views/slide16"));

// Fallback component while slides are loading
const SlideFallback = () => (
  <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
    Loading...
  </div>
);

function User() {
  const { user, chimeraserv, filter } = useAppContext();
  const [title] = useState("Dashboard | Chimera Solutions | Insights");
  const [fields, setFields] = useState({
    sales: { data: [], state: false },
    session: { data: [], state: false },
    brands: { data: [], state: false },
    chains: { data: [], state: false },
    payType: { data: [], state: false },
    revCent: { data: [], state: false },
    group: { data: [], state: false },
    currency: { base: {}, state: false, modified: null },
    load: false,
  });
  const [voidsData, setVoidsData] = useState(null);
  const [discountsData, setDiscountsData] = useState(null);
  const [deletionData, setDeletionData] = useState(null);

  // Helper to update fields
  const updateField = useCallback((key, value) => {
    setFields((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);
  const refreshDashboard = useCallback(() => {
    window.location.reload();
    // Optionally reset other fields if you want a full reload
    // The useEffect will refetch data when load is false
  }, []);

  // Add this function to generate dashboard filter
  const generateDashboardFilter = useCallback(() => {
    if (!fields.brands.state || !fields.chains.state || !fields.revCent.state) {
      return null;
    }

    // Get today's start and end timestamps
    const today = new Date();
    const startOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const endOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999
    );

    const filterData = {
      // grpKey: { $in: fields.group.data.map((group) => group.grpKey) },
      braKey: { $in: fields.brands.data.map((brand) => brand.braKey) },
      chaKey: { $in: fields.chains.data.map((chain) => chain.chaKey) },
      // revCent: { $in: fields.revCent.data.map((revCent) => revCent.name) },
      date: {
        $gt: Math.floor(startOfDay.getTime() / 1000), // Today start timestamp
        $lt: Math.floor(endOfDay.getTime() / 1000), // Today end timestamp
      },
    };

    return window.btoa(JSON.stringify(filterData));
  }, [fields.brands, fields.chains, fields.revCent, fields.group]);

  // Add this function after getVoidsData and before getDiscountsData
  const getDeletionData = useCallback(async () => {
    if (!fields.load) return null;

    const headers = {
      Authorization: user.data.usrKey,
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    try {
      const dashboardBase64 = generateDashboardFilter();

      // Add null check for dashboard filter
      if (!dashboardBase64) {
        return {
          brands: fields.brands,
          chains: fields.chains,
          deletions: { data: [] },
        };
      }

      const response = await axios.post(
        chimeraserv.urlGet + "/deletion/set/0/",
        { id: JSON.stringify(dashboardBase64) },
        { headers }
      );

      const deletionSales = response.data || [];

      return {
        brands: fields.brands,
        chains: fields.chains,
        deletions: { data: deletionSales },
      };
    } catch (error) {
      console.error("Error fetching deletion data:", error);
      return {
        brands: fields.brands,
        chains: fields.chains,
        deletions: { data: [] },
      };
    }
  }, [fields, user.data, chimeraserv.urlGet, generateDashboardFilter]);

  const getDiscountsData = useCallback(async () => {
    if (!fields.load) return null;

    try {
      const dashboardBase64 = generateDashboardFilter();

      // Add null check for dashboard filter
      if (!dashboardBase64) {
        return {
          brands: fields.brands,
          chains: fields.chains,
          discounts: { data: [] },
        };
      }

      const response = await fetch(`${chimeraserv.urlGet}/discount/set/0/`, {
        method: "POST",
        body: JSON.stringify({ id: dashboardBase64 }),
        headers: {
          Authorization: user.data.usrKey,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // let processedData = [];
      // if (result && Object.keys(result).length) {
      //   processedData = Array.isArray(result) ? result : [result];
      // }

      return {
        brands: fields.brands,
        chains: fields.chains,
        discounts: { data: result || [] },
      };
    } catch (error) {
      console.error("Error fetching discount data:", error);
      return {
        brands: fields.brands,
        chains: fields.chains,
        discounts: { data: [] },
      };
    }
  }, [fields, user.data, chimeraserv.urlGet, generateDashboardFilter]); // Add generateDashboardFilter dependency

  // Data fetching functions
  const getVoidsData = useCallback(async () => {
    if (!fields.load) return null;

    const headers = {
      Authorization: user.data.usrKey,
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    try {
      const dashboardBase64 = generateDashboardFilter();

      const response = await axios.post(
        chimeraserv.urlGet + "/void/set/0/",
        { id: JSON.stringify(dashboardBase64) },
        { headers }
      );

      const voidSales = response.data || [];

      return {
        brands: fields.brands,
        chains: fields.chains,
        voids: { data: voidSales },
      };
    } catch (error) {
      console.error("Error fetching void data:", error);
      return {
        brands: fields.brands,
        chains: fields.chains,
        voids: { data: [] },
      };
    }
  }, [fields, user.data, chimeraserv.urlGet]);

  const getCurrency = useCallback(
    async (headers, callback) => {
      try {
        const response = await axios.get(chimeraserv.urlGet + "/group", {
          headers,
        });
        let data = [];
        if (Object.keys(response.data).length) {
          data = response.data;
        }
        callback(data);
      } catch {
        callback([]);
      }
    },
    [chimeraserv.urlGet]
  );

  const getBrand = useCallback(
    async (headers, callback) => {
      try {
        const response = await axios.get(chimeraserv.urlGet + "/brand", {
          headers,
        });

        let data = [];
        if (Object.keys(response.data).length) {
          data = response.data;
        }

        callback(data);
      } catch {
        console.log("error");
        callback([]);
      }
    },
    [chimeraserv.urlGet]
  );

  const getChain = useCallback(
    async (headers, callback) => {
      try {
        const response = await axios.get(chimeraserv.urlGet + "/chain", {
          headers,
        });
        let data = [];
        if (Object.keys(response.data).length) {
          data = response.data;
        }
        callback(data);
      } catch {
        callback([]);
      }
    },
    [chimeraserv.urlGet]
  );

  const getPaytype = useCallback(
    async (headers, callback) => {
      try {
        const response = await axios.get(chimeraserv.urlGet + "/paytype", {
          headers,
        });
        let data = [];
        if (Object.keys(response.data).length) {
          data = response.data;
        }
        callback(data);
      } catch {
        callback([]);
      }
    },
    [chimeraserv.urlGet]
  );

  const getRevcent = useCallback(
    async (headers, callback) => {
      try {
        const response = await axios.get(chimeraserv.urlGet + "/revcent", {
          headers,
        });
        let data = [];
        if (Object.keys(response.data).length) {
          data = response.data;
        }
        callback(data);
      } catch {
        callback([]);
      }
    },
    [chimeraserv.urlGet]
  );

  const getGroup = useCallback(
    async (headers, callback) => {
      try {
        const response = await axios.get(chimeraserv.urlGet + "/item", {
          headers,
        });
        let data = [];
        if (Object.keys(response.data).length) {
          data = response.data;
        }
        callback(data);
      } catch {
        callback([]);
      }
    },
    [chimeraserv.urlGet]
  );

  const getSales = useCallback(
    async (headers, callback) => {
      try {
        const response = await axios.get(chimeraserv.urlGet + "/sales/today", {
          headers,
        });

        const { groups, branches, chains, sessions, sales } = response.data;
        let allSales = [];
        let data = [];
        for (const branch of branches) {
          for (const chain of chains) {
            const openSessions = sessions.filter(
              (session) =>
                String(session.braKey) === String(branch) &&
                String(session.chaKey) === String(chain)
            );
            const sessionNumberList = openSessions.map(
              (openSession) => openSession.sesNo
            );
            const openSessionSales = sales.filter(
              (sale) =>
                String(sale.braKey) === String(branch) &&
                String(sale.chaKey) === String(chain) &&
                sessionNumberList.includes(sale.sesNo)
            );
            allSales = allSales.concat(openSessionSales);
          }
        }
        if (Object.keys(allSales).length) {
          data = allSales;
        }
        callback(data);
      } catch {
        callback([]);
      }
    },
    [chimeraserv.urlGet]
  );

  const getSessions = useCallback(
    async (headers, callback) => {
      try {
        const response = await axios.post(
          chimeraserv.urlGet + "/session/all/today",
          { headers }
        );
        let data = [];
        if (Object.keys(response.data).length) {
          data = response.data;
        }
        callback(data);
      } catch {
        callback([]);
      }
    },
    [chimeraserv.urlGet]
  );

  useEffect(() => {
    if (fields.load) {
      // Fetch all three data requests in parallel
      Promise.all([getVoidsData(), getDiscountsData(), getDeletionData()]).then(
        ([voidsResult, discountsResult, deletionResult]) => {
          setVoidsData(voidsResult);
          setDiscountsData(discountsResult);
          setDeletionData(deletionResult);
        }
      );
    }
  }, [fields.load, getVoidsData, getDiscountsData, getDeletionData]);

  // Fetch data on mount - Using Promise.all for parallel requests
  useEffect(() => {
    if (user.data) {
      const headers = {
        Authorization: user.data.usrKey,
        "Content-Type": "application/json",
        Accept: "application/json",
      };

      // Create wrapper functions that return promises
      const getCurrencyPromise = () =>
        new Promise((resolve) => getCurrency(headers, resolve));
      const getBrandPromise = () =>
        new Promise((resolve) => getBrand(headers, resolve));
      const getChainPromise = () =>
        new Promise((resolve) => getChain(headers, resolve));
      const getRevcentPromise = () =>
        new Promise((resolve) => getRevcent(headers, resolve));
      const getPaytypePromise = () =>
        new Promise((resolve) => getPaytype(headers, resolve));
      const getSalesPromise = () =>
        new Promise((resolve) => getSales(headers, resolve));
      const getSessionsPromise = () =>
        new Promise((resolve) => getSessions(headers, resolve));
      const getGroupPromise = () =>
        new Promise((resolve) => getGroup(headers, resolve));

      // Fetch all data in parallel
      Promise.all([
        getCurrencyPromise(),
        getBrandPromise(),
        getChainPromise(),
        getRevcentPromise(),
        getPaytypePromise(),
        getSalesPromise(),
        getSessionsPromise(),
        getGroupPromise(),
      ])
        .then(
          ([
            currencyData,
            brandData,
            chainData,
            revcentData,
            paytypeData,
            salesData,
            sessionData,
            groupData,
          ]) => {
            // Batch all state updates into a single call
            const array = [];
            const currency = currencyData
              .map((group) => group.baseCurr)
              .filter((currency) => !array.includes(currency));
            const groupModified = currencyData[0]?.modified
              ? currencyData[0].modified * 1000
              : null;

            setFields((prev) => ({
              ...prev,
              currency: {
                base: currency[0] ? currency[0]?.toUpperCase() : "QAR",
                state: true,
                modified: groupModified,
              },
              brands: {
                data: brandData,
                state: true,
              },
              chains: {
                data: chainData,
                state: true,
              },
              revCent: {
                data: revcentData,
                state: true,
              },
              payType: {
                data: paytypeData,
                state: true,
              },
              sales: {
                data: salesData,
                state: true,
              },
              session: {
                data: sessionData,
                state: true,
              },
              group: {
                data: groupData,
                state: true,
              },
            }));
          }
        )
        .catch((error) => {
          console.error("Error fetching dashboard data:", error);
        });
    }
  }, [
    user.data,
    getCurrency,
    getBrand,
    getChain,
    getRevcent,
    getPaytype,
    getSales,
    getSessions,
    getGroup,
  ]);

  // blockFilter logic
  useEffect(() => {
    if (
      !fields.load &&
      fields.session.state &&
      fields.payType.state &&
      fields.revCent.state &&
      fields.chains.state &&
      fields.brands.state &&
      fields.group.state &&
      fields.sales.state &&
      fields.currency.state
    ) {
      setFields((prev) => ({
        ...prev,
        load: true,
      }));
    }
  }, [
    fields.session.state,
    fields.payType.state,
    fields.revCent.state,
    fields.chains.state,
    fields.brands.state,
    fields.group.state,
    fields.sales.state,
    fields.currency.state,
    fields.load,
  ]);

  // getExpiringChains logic
  const getExpiringChains = useCallback(
    (chains) => {
      const today = new Date();
      today.setHours(0, 0, 0);

      const oneMonthLaterDate = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        today.getDate()
      );

      const todayTS = parseInt(today.setDate(today.getDate()) / 1000);
      const oneMonthLaterTS = parseInt(
        oneMonthLaterDate.setDate(oneMonthLaterDate.getDate()) / 1000
      );

      const chainsWithExpiration = chains?.filter(
        (item) => item?.outletExp && item.outletExp <= oneMonthLaterTS
      );

      if (!chainsWithExpiration || chainsWithExpiration.length === 0) {
        return [];
      }

      const chainsWithExpNote = chainsWithExpiration?.map((item) => {
        if (item.outletExp <= todayTS) {
          return {
            ...item,
            status: "expired",
            message: `${
              fields?.brands?.data?.find(
                (brandItem) => brandItem.braKey === item.braKey
              )?.name
            } - ${item.name} : subscription has been expired since ${new Date(
              item.outletExp * 1000
            ).toDateString()}`,
          };
        }
        if (item.outletExp >= todayTS && item.outletExp <= oneMonthLaterTS) {
          return {
            ...item,
            status: "expiring",
            message: `${
              fields?.brands?.data?.find(
                (brandItem) => brandItem.braKey === item.braKey
              )?.name
            } - ${item.name} : subscription will expire on ${new Date(
              item.outletExp * 1000
            ).toDateString()}`,
          };
        }
        return item;
      });

      return chainsWithExpNote;
    },
    [fields?.brands?.data]
  );

  const date = fields.modified || new Date().getTime();

  return (
    <section>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className={["viewTitle"]}>
        Dashboard <span className={"italic"}>(Today)</span>{" "}
        <span>(updated last: {formatDate.long(date / 1000)})</span>
        <button className={"refreshButton"} onClick={refreshDashboard}>
          <FontAwesomeIcon
            className={["icon"].join(" ")}
            icon={["fas", "refresh"]}
          />
        </button>
        {getExpiringChains(fields?.chains?.data)?.length > 0 && (
          <ExpirinOutletsModal
            expiringChains={getExpiringChains(fields?.chains?.data)}
          />
        )}
      </div>
      <div className={["viewBody"]}>
        {/* <Slide15 data={fields.load ? fields : null} /> */}
        {/* <Slide16 data={fields.load ? fields : null} /> */}

        <Suspense fallback={<SlideFallback />}>
          <div className={["report"]}>
            <Window
              user={user}
              chimeraserv={chimeraserv}
              title={"Sales Summary"}
            >
              <Slide1 data={fields.load ? fields : null} />
            </Window>

            <div className={["contentContainer"]}>
              <div className={["contentRow"]} style={{ width: "32.75%" }}>
                <Window
                  user={user}
                  chimeraserv={chimeraserv}
                  title={"Total Customers"}
                  horizontal
                >
                  <Slide7 data={fields.load ? fields : null} />
                </Window>
              </div>
              <div className={["contentRow"]} style={{ width: "32.75%" }}>
                <Window
                  user={user}
                  chimeraserv={chimeraserv}
                  title={"Total Checks"}
                  horizontal
                >
                  <Slide8 data={fields.load ? fields : null} />
                </Window>
              </div>
              <div className={["contentRow"]} style={{ width: "32.75%" }}>
                <Window
                  user={user}
                  chimeraserv={chimeraserv}
                  title={"Total Sales"}
                  horizontal
                >
                  <Slide9 data={fields.load ? fields : null} />
                </Window>
              </div>
            </div>

            <div className={["contentContainer"]}>
              <div className={["contentRow"]} style={{ width: "49.55%" }}>
                <Window
                  user={user}
                  chimeraserv={chimeraserv}
                  title={"Revenue Center"}
                  horizontal
                >
                  <Slide2 data={fields.load ? fields : null} />
                </Window>
              </div>
              <div className={["contentRow"]} style={{ width: "49.55%" }}>
                <Window
                  user={user}
                  chimeraserv={chimeraserv}
                  title={"Payment Type"}
                  horizontal
                >
                  <Slide3 data={fields.load ? fields : null} />
                </Window>
              </div>
            </div>

            <Window
              user={user}
              chimeraserv={chimeraserv}
              title={"Sales by Group Items"}
            >
              <Slide10 data={fields.load ? fields : null} />
            </Window>
            <div className={["contentContainer"]}>
              <div className={["contentRow"]} style={{ width: "100%" }}>
                <Window
                  user={user}
                  chimeraserv={chimeraserv}
                  title={"Top 20 Selling Items"}
                  horizontal
                >
                  <Slide5 data={fields.load ? fields : null} />
                </Window>
              </div>
            </div>
            <div
              className={["contentContainerColumn"]}
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "stretch",
              }}
            >
              {/* Left column: Sales Statistics, Total Voids, Total Discounts, Total Deletions */}
              <div
                style={{
                  flex: "1 1 auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div className={["contentContainer"]}>
                  <div className={["contentRow"]} style={{ width: "49%" }}>
                    <Window
                      user={user}
                      chimeraserv={chimeraserv}
                      title={"Hourly Sales"}
                      horizontal
                    >
                      <Slide11 data={fields.load ? fields : null} />
                    </Window>
                  </div>
                  <div className={["contentRow"]} style={{ width: "49%" }}>
                    <Window
                      user={user}
                      chimeraserv={chimeraserv}
                      title={"Sales Statistics"}
                      horizontal
                    >
                      <Slide4 data={fields.load ? fields : null} />
                    </Window>
                  </div>
                </div>

                <div className={["contentContainer"]}>
                  <div className={["contentRow"]} style={{ width: "30%" }}>
                    <Window
                      user={user}
                      chimeraserv={chimeraserv}
                      title={"Total Discounts"}
                      horizontal
                    >
                      <Slide13 data={discountsData} />
                    </Window>
                  </div>
                  <div className={["contentRow"]} style={{ width: "30%" }}>
                    <Window
                      user={user}
                      chimeraserv={chimeraserv}
                      title={"Total Deletions"}
                      horizontal
                    >
                      <Slide14 data={deletionData} />
                    </Window>
                  </div>
                  <div className={["contentRow"]} style={{ width: "30%" }}>
                    <Window
                      user={user}
                      chimeraserv={chimeraserv}
                      title={"Total Voids"}
                      horizontal
                    >
                      <Slide12 data={voidsData} />
                    </Window>
                  </div>
                </div>
              </div>

              {/* Right column: Hourly Sales */}
            </div>
          </div>
        </Suspense>
      </div>
    </section>
  );
}

export default User;
